// アクセス解析（閲覧・クリック位置・スクロール）。送り先は自社サーバー（/api/public/stats）だけで、外部には送らない。
// Cookie・localStorage は使わない。フォームの入力内容は取らない（入力欄を押した位置だけ）。
// 記録を望まない設定（Do Not Track・Global Privacy Control）のブラウザでは何もしない。
// サーバー側の受け取りと保存期間は app/analytics、利用者への説明はプライバシーポリシーの「アクセス情報の取得と利用」。

const ENDPOINT = "/api/public/stats";

type Payload = Record<string, string | number>;

let queue: Payload[] = [];
let pageId = "";
let path = "";
let previousPath = "";
let maxScroll = 0;
let installed = false;

function enabled(): boolean {
  // 管理画面のヒートマップでページを枠内に表示しているときは記録しない
  if (window.top !== window.self) return false;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  const w = window as Window & { doNotTrack?: string };
  if (nav.globalPrivacyControl === true || nav.doNotTrack === "1" || w.doNotTrack === "1") return false;
  return !/^\/(admin|preview)(\/|$)/.test(window.location.pathname);
}

function randomId(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => (b % 36).toString(36)).join("");
}

const viewport = () => ({ vw: window.innerWidth, vh: window.innerHeight });
const documentSize = () => ({
  dw: document.documentElement.scrollWidth,
  dh: document.documentElement.scrollHeight,
});

function send(leaving: boolean) {
  while (queue.length > 0) {
    const body = JSON.stringify({ e: queue.splice(0, 60) });
    // ページを離れるときは、離れた後も送り切れる sendBeacon を使う
    if (leaving && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
    } else {
      void fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
        credentials: "same-origin",
      }).catch(() => undefined);
    }
  }
}

function updateScroll() {
  const { dh } = documentSize();
  const reached = Math.round(((window.scrollY + window.innerHeight) / Math.max(dh, 1)) * 100);
  maxScroll = Math.max(maxScroll, Math.min(100, reached));
}

function flushScroll() {
  if (pageId && maxScroll > 0) {
    queue.push({ k: "scroll", p: path, id: pageId, sc: maxScroll, ...viewport(), ...documentSize() });
    maxScroll = 0;
  }
}

// ページの中での要素の位置。管理画面のヒートマップで同じ要素を探し当てるのに使う
function cssPath(el: Element): string {
  const parts: string[] = [];
  let cur: Element | null = el;
  while (cur && cur !== document.body && cur !== document.documentElement && parts.length < 25) {
    let part = cur.tagName.toLowerCase();
    // React が作る ":r1:" のような ID は毎回変わるので使わない
    if (cur.id && /^[A-Za-z][\w-]*$/.test(cur.id)) {
      parts.unshift(`#${cur.id}`);
      return parts.join(" > ");
    }
    const parent: Element | null = cur.parentElement;
    if (parent) {
      const sameTag = Array.from(parent.children).filter((c) => c.tagName === cur!.tagName);
      if (sameTag.length > 1) part += `:nth-of-type(${sameTag.indexOf(cur) + 1})`;
    }
    parts.unshift(part);
    cur = parent;
  }
  return ["body", ...parts].join(" > ");
}

function labelOf(el: Element): string {
  return (el.getAttribute("aria-label") || el.textContent || "").replace(/\s+/g, " ").trim().slice(0, 60);
}

// 押したものの種類（問い合わせにつながる電話・LINE・メール・地図を区別する）
function describe(el: Element): { t: string; h: string; l: string } {
  const tracked = el.closest("[data-track]");
  const action = el.closest(
    "a, button, [role=button], input, textarea, select, label, summary",
  );
  if (action && ["INPUT", "TEXTAREA", "SELECT"].includes(action.tagName)) {
    return { t: "form", h: "", l: "" }; // 入力欄は位置だけ（入力内容・表示中の値は取らない）
  }
  if (tracked) {
    return { t: tracked.getAttribute("data-track") ?? "", h: "", l: labelOf(tracked) };
  }
  if (action instanceof HTMLAnchorElement) {
    const raw = action.getAttribute("href") ?? "";
    const href = action.href;
    let t = "internal";
    if (raw.startsWith("tel:")) t = "tel";
    else if (raw.startsWith("mailto:")) t = "mail";
    else {
      const url = new URL(href, window.location.href);
      const host = url.hostname;
      const isGoogleMaps =
        /^maps\.google\./.test(host) ||
        host === "maps.app.goo.gl" ||
        (host === "goo.gl" && url.pathname.startsWith("/maps")) ||
        (/(^|\.)google\.[a-z.]+$/.test(host) && url.pathname.startsWith("/maps"));
      if (host === "line.me" || host.endsWith(".line.me") || host === "lin.ee") t = "line";
      else if (isGoogleMaps) t = "map";
      else if (url.origin !== window.location.origin) t = "external";
    }
    return { t, h: t === "tel" || t === "mail" ? raw : href, l: labelOf(action) };
  }
  if (action) return { t: action.tagName === "LABEL" ? "form" : "button", h: "", l: labelOf(action) };
  return { t: "", h: "", l: "" };
}

function onClick(e: MouseEvent) {
  if (!pageId || !enabled() || !(e.target instanceof Element)) return;
  const el = e.target;
  if (el.closest("[data-noanalytics]")) return;
  const { dw, dh } = documentSize();
  const rect = el.getBoundingClientRect();
  const { t, h, l } = describe(el);
  queue.push({
    k: "click",
    p: path,
    id: pageId,
    x: e.pageX / Math.max(dw, 1),
    y: e.pageY,
    ox: rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0,
    oy: rect.height > 0 ? (e.clientY - rect.top) / rect.height : 0,
    s: cssPath(el),
    t,
    h,
    l,
    ...viewport(),
    dw,
    dh,
  });
  // 電話・LINE・外部サイトなどはページを離れるので、すぐ送る
  if (t !== "" && t !== "internal" && t !== "button" && t !== "form") {
    flushScroll();
    send(true);
  }
}

function install() {
  if (installed) return;
  installed = true;
  document.addEventListener("click", onClick, { capture: true, passive: true });
  let scrollTimer = 0;
  window.addEventListener(
    "scroll",
    () => {
      if (scrollTimer) return;
      scrollTimer = window.setTimeout(() => {
        scrollTimer = 0;
        updateScroll();
      }, 300);
    },
    { passive: true },
  );
  const leave = () => {
    updateScroll();
    flushScroll();
    send(true);
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") leave();
  });
  window.addEventListener("pagehide", leave);
  window.setInterval(() => send(false), 10000);
}

/** ページの表示を記録する（画面遷移のたびに呼ぶ） */
export function trackPageview(pathname: string) {
  if (!enabled()) return;
  install();
  updateScroll();
  flushScroll();
  // 最初の表示はブラウザの参照元、サイト内の画面遷移は直前のページ
  const referrer = previousPath ? window.location.origin + previousPath : document.referrer;
  pageId = randomId();
  path = pathname;
  previousPath = pathname;
  maxScroll = 0;
  queue.push({ k: "pageview", p: path, id: pageId, r: referrer, ...viewport(), ...documentSize() });
  send(false);
  // 画面の高さは表示直後より後で決まるので、少し待ってから最初の到達位置を測る
  window.setTimeout(updateScroll, 1500);
}
